import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaTruck, FaPlane, FaTrain, FaShip, FaWarehouse, FaBoxOpen, FaGlobeAmericas, FaShieldAlt } from 'react-icons/fa';

const ServicesPage = () => {
  const transportServices = [
    {
      id: 'road',
      icon: <FaTruck size={48} />,
      title: 'Road Transport',
      description: 'Our extensive fleet of vehicles ensures timely delivery across the country. From small parcels to full truckloads, we handle shipments of all sizes with care and efficiency.',
      features: [
        'Full Truckload (FTL) Services',
        'Less than Truckload (LTL) Services',
        'Express Delivery Options',
        'Temperature-Controlled Transport',
      ]
    },
    {
      id: 'air',
      icon: <FaPlane size={48} />,
      title: 'Air Freight',
      description: 'When time is of the essence, our air freight services provide the fastest delivery options. We partner with leading airlines to ensure your cargo reaches its destination quickly and safely.',
      features: [
        'International Air Freight',
        'Domestic Air Cargo',
        'Express Air Freight',
      ]
    },
    {
      id: 'train',
      icon: <FaTrain size={48} />,
      title: 'Rail Transport',
      description: 'Our rail transport solutions offer a cost-effective and environmentally friendly alternative for long-distance shipping. Ideal for bulk cargo and containers.',
      features: [
        'Container Rail Transport',
        'Bulk Cargo Solutions',
        'Intermodal Services',
        'Specialized Cargo Handling'
      ]
    },
    {
      id: 'ship',
      icon: <FaShip size={48} />,
      title: 'Ocean Freight',
      description: 'For international shipping, our ocean freight services provide reliable and economical solutions. We handle everything from documentation to customs clearance.',
      features: [
        'Full Container Load (FCL)',
        'Less than Container Load (LCL)',
        'Break Bulk Cargo',
        'Project Cargo Handling'
      ]
    }
  ];

  const additionalServices = [
    {
      id: 'warehouse',
      icon: <FaWarehouse size={48} />,
      title: 'Warehousing Solutions',
      description: 'Our state-of-the-art warehousing facilities offer secure storage for your goods. With advanced inventory management systems, we ensure your products are stored safely and efficiently.',
      features: [
        'Short and Long-term Storage',
        'Inventory Management',
        'Order Fulfillment',
      ]
    },
    {
      id: 'packaging',
      icon: <FaBoxOpen size={48} />,
      title: 'Packaging & Distribution',
      description: 'We provide comprehensive packaging and distribution services to ensure your products reach customers in perfect condition. Our team handles everything from packaging design to final delivery.',
      features: [
        'Custom Packaging Solutions',
        'Kitting and Assembly',
        'Distribution Center Management',
        'E-commerce Fulfillment',
        'Returns Management'
      ]
    },
    {
      id: 'customs',
      icon: <FaGlobeAmericas size={48} />,
      title: 'Customs Clearance',
      description: 'Our experienced team navigates the complexities of international shipping regulations to ensure smooth customs clearance for your shipments, minimizing delays and complications.',
      features: [
        'Import/Export Documentation',
        'Customs Brokerage',
        'Duty and Tax Management',
        'Compliance Consulting',
        'Trade Regulation Guidance'
      ]
    },
    {
      id: 'security',
      icon: <FaShieldAlt size={48} />,
      title: 'Cargo Insurance',
      description: 'Protect your shipments with our comprehensive cargo insurance options. We offer tailored coverage to safeguard your goods against loss, damage, or theft during transit.',
      features: [
        'All-Risk Coverage',
        'Door-to-Door Protection',
        'High-Value Cargo Insurance',
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Our Services | VEDUS Supply Chain Management</title>
        <meta name="description" content="Explore VEDUS Supply Chain Management's comprehensive logistics services including road, air, rail, and ocean freight, warehousing, packaging, customs clearance, and cargo insurance." />
      </Helmet>
      
      <div className="bg-secondary-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Our Services</h1>
          <p className="text-xl text-secondary-300 max-w-3xl mx-auto">
            VEDUS offers end-to-end logistics solutions tailored to your specific needs. From transportation and warehousing to packaging and customs clearance, we handle every aspect of your supply chain.
          </p>
        </div>
      </div>
      
      {/* Transport Services Section */}
      <section className="py-16 bg-white dark:bg-secondary-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-secondary-900 dark:text-white">Transport Services</h2>
            <p className="max-w-2xl mx-auto text-secondary-600 dark:text-secondary-300">
              We offer multiple transportation modes to ensure your cargo reaches its destination efficiently and on time.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {transportServices.map((service, index) => (
              <motion.div 
                key={service.id}
                className="bg-secondary-50 dark:bg-secondary-700 rounded-lg shadow-lg overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="p-6 bg-primary-600 dark:bg-primary-700 text-white flex items-center">
                  <div className="mr-4">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-bold">{service.title}</h3>
                </div>
                <div className="p-6">
                  <p className="text-secondary-600 dark:text-secondary-300 mb-6">
                    {service.description}
                  </p>
                  <h4 className="font-semibold text-lg mb-3 text-secondary-900 dark:text-white">Key Features:</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="h-5 w-5 text-primary-600 dark:text-primary-400 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-secondary-600 dark:text-secondary-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Additional Services Section */}
      <section className="py-16 bg-secondary-50 dark:bg-secondary-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-secondary-900 dark:text-white">Value-Added Services</h2>
            <p className="max-w-2xl mx-auto text-secondary-600 dark:text-secondary-300">
              Beyond transportation, we offer a range of complementary services to streamline your supply chain operations.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div 
                key={service.id}
                className="bg-white dark:bg-secondary-800 rounded-lg shadow-lg overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="p-6 border-b border-secondary-200 dark:border-secondary-700 flex items-center">
                  <div className="mr-4 text-primary-600 dark:text-primary-400">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-secondary-900 dark:text-white">{service.title}</h3>
                </div>
                <div className="p-6">
                  <p className="text-secondary-600 dark:text-secondary-300 mb-6">
                    {service.description}
                  </p>
                  <h4 className="font-semibold text-lg mb-3 text-secondary-900 dark:text-white">Services Include:</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="h-5 w-5 text-primary-600 dark:text-primary-400 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-secondary-600 dark:text-secondary-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Optimize Your Supply Chain?</h2>
          <p className="max-w-2xl mx-auto mb-8 text-primary-100">
            Contact us today to discuss your logistics needs and discover how VEDUS can help streamline your operations.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a 
              href="/quote" 
              className="px-8 py-3 bg-white text-primary-600 font-medium rounded-md hover:bg-primary-50 transition-colors duration-300"
            >
              Request a Quote
            </a>
            <a 
              href="/contact" 
              className="px-8 py-3 bg-primary-700 text-white font-medium rounded-md hover:bg-primary-800 transition-colors duration-300"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicesPage;